import { Alert, Box, CircularProgress } from "@mui/material";
import { type TreeItemProps } from "@mui/x-tree-view";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type DragEvent,
  type FC,
} from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import {
  ArchiveFolderModal,
  type ArchiveFolderData,
} from "../../modules/assets/archive-folder-modal";
import { AssetPreviewModal } from "../../modules/assets/asset-preview-modal";
import { ContentLibraryAssets } from "../../modules/assets/components/content-library-assets";
import { ContentLibraryFilter } from "../../modules/assets/components/content-library-filter";
import { ContentLibraryHeader } from "../../modules/assets/components/content-library-header";
import { ContentLibrarySidebar } from "../../modules/assets/components/content-library-sidebar";
import { CustomTreeItem } from "../../modules/assets/components/custom-tree-item";
import { FolderBreadcrumb } from "../../modules/assets/components/folder-breadcrumb";
import { useUpdateFolderParentCallback } from "../../modules/assets/components/folder-parent";
import { ContentTypeChips } from "../../modules/assets/content-type-chips";
import { contentTypes as allContentTypes } from "../../modules/assets/content-types";
import { CreateFolderModal, type CreateFolderData } from "../../modules/assets/create-folder-modal";
import { EmptyState } from "../../modules/assets/empty-state";
import { useAssets } from "../../modules/assets/hooks/use-assets";
import { useFolders } from "../../modules/assets/hooks/use-folders";
import { MoveFolderModal, type MoveFolderData } from "../../modules/assets/move-folder-modal";
import {
  contentLibraryReducer,
  filterAssets,
  initialState,
} from "../../modules/assets/reducer/reducer";
import type { Asset } from "../../modules/assets/server/digital-asset-service";
import { SupabaseDigitalAssetService } from "../../modules/assets/server/digital-asset-service";
import type { Folder } from "../../modules/assets/server/folder-service";
import { TagChips } from "../../modules/assets/tag-chips";
import { UploadNewFileModal } from "../../modules/assets/upload-new-file-modal";
import {
  constructBreadcrumbs,
  folderNameFromId,
  folderParentMap,
  getDescendantFolderIds,
  ROOT_ID,
  transformFoldersToTreeItems,
} from "../../modules/assets/utils";
import { useAccount } from "../../modules/generic/hooks/use-account";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { SearchField } from "../../modules/search/search-field";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { useDialog } from "../../utils/hooks/use-dialog";
import type { UUID } from "../../utils/uuid";

// TODO: This will need changing once the tags are fetched from the backend
function extractAvailableTags(assets: Asset[]): string[] {
  return Array.from(
    new Set(
      assets.filter((asset) => asset.tags && asset.tags.length > 0).flatMap((asset) => asset.tags!),
    ),
  ).sort();
}

const ASSET_DRAG_TYPE = "application/x-changeengine-asset";
const FOLDER_DRAG_TYPE = "application/x-changeengine-folder";

const ContentLibraryPage: FC = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(contentLibraryReducer, initialState);
  const [searchFieldKey, setSearchFieldKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const previewModal = useDialog();
  const uploadModal = useDialog();
  const createFolderModal = useDialog<CreateFolderData>();
  const archiveFolderModal = useDialog<ArchiveFolderData>();
  const moveFolderModal = useDialog<MoveFolderData>();
  const { handleOpen: openCreateFolderModal } = createFolderModal;
  const { handleOpen: openArchiveFolderModal } = archiveFolderModal;
  const { handleOpen: openMoveFolderModal } = moveFolderModal;

  const { data, loading, error, reload } = useAssets();
  const {
    data: foldersData,
    loading: foldersLoading,
    error: foldersError,
    reload: reloadFolders,
  } = useFolders();
  const account = useAccount();
  const updateFolderParent = useUpdateFolderParentCallback();

  const moveAsset = useSupabaseCallback(
    async ({ supabase }, assetId: UUID, targetFolderId: UUID | null) => {
      const service = new SupabaseDigitalAssetService(supabase);
      const { error: updateError } = await service.update(assetId, { folder_id: targetFolderId });
      if (updateError) throw updateError;
    },
    [],
  );

  useEffect(() => {
    const formats = searchParams.has("format") ? searchParams.getAll("format") : [];
    const tags = searchParams.has("tags") ? searchParams.getAll("tags") : [];
    const previewAssetId = searchParams.get("preview");
    const selectedFolderId = searchParams.get("folder") ?? ROOT_ID;

    dispatch({
      type: "SYNC_FROM_URL",
      payload: { contentTypes: formats, tags, previewAssetId, selectedFolderId },
    });
  }, [searchParams]);

  useEffect(() => {
    if (state.previewAssetId && !previewModal.open) {
      previewModal.handleOpen();
    } else if (!state.previewAssetId && previewModal.open) {
      previewModal.handleClose();
    }
  }, [state.previewAssetId, previewModal]);

  const selectedAsset = useMemo(() => {
    if (!state.previewAssetId || loading || !data) return null;
    return data.find((x) => x.id === state.previewAssetId) ?? null;
  }, [state.previewAssetId, data, loading]);

  const treeItems = useMemo(() => {
    if (!foldersData || foldersError || foldersLoading) return [];
    return transformFoldersToTreeItems(foldersData ?? []);
  }, [foldersData, foldersError, foldersLoading]);

  const folderMap = useMemo(() => {
    return folderParentMap(foldersData);
  }, [foldersData]);

  useEffect(() => {
    if (!state.selectedTreeItemId || treeItems.length === 0) return;

    const findPath = (
      items: typeof treeItems,
      targetId: string,
      path: string[],
    ): string[] | null => {
      for (const item of items) {
        const currentPath = [...path, item.id];
        if (item.id === targetId) return currentPath;
        if (item.children?.length) {
          const result = findPath(item.children, targetId, currentPath);
          if (result) return result;
        }
      }
      return null;
    };

    const path = findPath(treeItems, state.selectedTreeItemId, []);
    if (!path) return;

    const parentIds = path.slice(0, -1);
    const nextExpanded = Array.from(new Set([...state.expandedItems, ...parentIds]));
    if (nextExpanded.length !== state.expandedItems.length) {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: nextExpanded });
    }
  }, [state.selectedTreeItemId, state.expandedItems, treeItems]);

  // TODO: This will need changing once the tags are fetched from the backend
  const availableTags = useMemo(() => {
    if (!data) return [];
    return extractAvailableTags(data);
  }, [data]);

  // Compute filtered assets based on current state
  const filteredData = useMemo(() => {
    if (!data) return null;
    return filterAssets(
      data,
      {
        selectedTreeItemId: state.selectedTreeItemId,
        appliedContentTypes: state.appliedContentTypes,
        appliedTags: state.appliedTags,
        searchText: state.searchText,
      },
      treeItems,
    );
  }, [
    data,
    state.selectedTreeItemId,
    state.appliedContentTypes,
    state.appliedTags,
    state.searchText,
    treeItems,
  ]);

  const filteredAllData = useMemo(() => {
    if (!data) return [];
    if (!state.searchText.trim()) return [];
    return filterAssets(
      data,
      {
        selectedTreeItemId: null,
        appliedContentTypes: state.appliedContentTypes,
        appliedTags: state.appliedTags,
        searchText: state.searchText,
      },
      treeItems,
    );
  }, [data, state.appliedContentTypes, state.appliedTags, state.searchText, treeItems]);

  const filteredFolders = useMemo(() => {
    if (!foldersData) return [];

    const folderId = searchParams.get("folder");

    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return foldersData
      .filter((f) => f.parent_folder_id === (folderId === ROOT_ID ? null : folderId))
      .filter((f) => f.name.toLowerCase().includes(state.searchText.trim().toLowerCase()))
      .sort((a, b) => naturalCollator.compare(a.name, b.name));
  }, [foldersData, searchParams, state.searchText]);

  const filteredAllFolders = useMemo(() => {
    if (!foldersData) return [];
    const searchLower = state.searchText.trim().toLowerCase();
    if (!searchLower) return [];
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    return foldersData
      .filter((f) => f.name.toLowerCase().includes(searchLower))
      .sort((a, b) => naturalCollator.compare(a.name, b.name));
  }, [foldersData, state.searchText]);

  const breadcrumbs = useMemo(() => {
    return constructBreadcrumbs(treeItems, state.selectedTreeItemId);
  }, [treeItems, state.selectedTreeItemId]);

  // Drag and drop handlers
  const handleAssetDragStart = useCallback((asset: Asset, event: DragEvent): void => {
    event.dataTransfer.setData(ASSET_DRAG_TYPE, asset.id);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleFolderDragStart = useCallback((folder: Folder, event: DragEvent): void => {
    event.dataTransfer.setData(FOLDER_DRAG_TYPE, folder.id);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleSidebarFolderDragStart = useCallback((folderId: UUID, event: DragEvent): void => {
    event.dataTransfer.setData(FOLDER_DRAG_TYPE, folderId);
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleFolderDragOver = useCallback((event: DragEvent): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const handleFolderDrop = useCallback(
    async (targetFolderId: UUID | null, event: DragEvent): Promise<void> => {
      event.preventDefault();
      event.stopPropagation();

      const assetId = event.dataTransfer.getData(ASSET_DRAG_TYPE);
      const folderId = event.dataTransfer.getData(FOLDER_DRAG_TYPE);

      // Handle asset drop
      if (assetId && data) {
        const asset = data.find((a) => a.id === assetId);
        if (!asset) return;

        // Don't drop on the same folder
        if (asset.folder_id === targetFolderId) return;
        if (asset.folder_id === null && targetFolderId === null) return;

        try {
          await moveAsset(assetId as UUID, targetFolderId);
          const folderName =
            folderNameFromId(targetFolderId ?? ROOT_ID, foldersData) ?? "Content Library";
          toast.success(t(`Asset moved to '${folderName}'`));
          reload();
        } catch (err) {
          console.error("Failed to move asset:", err);
          toast.error("Failed to move asset");
        }
        return;
      }

      // Handle folder drop
      if (folderId && foldersData) {
        const folder = foldersData.find((f) => f.id === folderId);
        if (!folder) return;

        // Can't drop on itself
        if (folder.id === targetFolderId) return;

        // Can't drop on current parent (no change)
        if (folder.parent_folder_id === targetFolderId) return;
        if (folder.parent_folder_id === null && targetFolderId === null) return;

        // Can't drop on a descendant
        if (targetFolderId) {
          const descendants = getDescendantFolderIds(folder.id, foldersData);
          if (descendants.has(targetFolderId)) {
            toast.error("Cannot move a folder into its own subfolder");
            return;
          }
        }

        try {
          const result = await updateFolderParent(folder.id, targetFolderId);
          if (result?.success) {
            const folderName =
              folderNameFromId(targetFolderId ?? ROOT_ID, foldersData) ?? "Content Library";
            toast.success(t(`Folder moved to '${folderName}'`));
            reloadFolders();
            reload();
          } else {
            throw new Error(result?.error ?? "Unknown error");
          }
        } catch (err) {
          console.error("Failed to move folder:", err);
          if (err instanceof Error && err.message.includes("folder_unique_name")) {
            toast.error("A folder with this name already exists in the destination");
          } else {
            toast.error("Failed to move folder");
          }
        }
      }
    },
    [data, foldersData, moveAsset, updateFolderParent, reload, reloadFolders, t],
  );

  const handleFolderSelect = useCallback(
    (folderId: string): void => {
      dispatch({ type: "SELECT_FOLDER", payload: folderId });

      const params = new URLSearchParams(searchParams);
      if (folderId === ROOT_ID) {
        params.delete("folder");
      } else {
        params.set("folder", folderId);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleSelectAsset = useCallback(
    (asset: Asset) => {
      dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: asset.id });
      const params = new URLSearchParams(searchParams);
      params.set("preview", asset.id);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleClosePreviewModal = useCallback(() => {
    dispatch({ type: "SET_PREVIEW_ASSET_ID", payload: null });
    const params = new URLSearchParams(searchParams);
    params.delete("preview");
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleArchived = useCallback(() => {
    handleClosePreviewModal();
    reload();
  }, [handleClosePreviewModal, reload]);

  const handleRemoveContentTypeFilter = useCallback(
    (contentType: string) => {
      dispatch({ type: "REMOVE_CONTENT_TYPE_FILTER", payload: contentType });

      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("format").filter((c) => c !== contentType);
      params.delete("format");
      newValues.forEach((val) => params.append("format", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleRemoveTagFilter = useCallback(
    (tag: string) => {
      dispatch({ type: "REMOVE_TAG_FILTER", payload: tag });

      const params = new URLSearchParams(searchParams);
      const newValues = params.getAll("tags").filter((tagValue) => tagValue !== tag);
      params.delete("tags");
      newValues.forEach((val) => params.append("tags", val));
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleApplyFilter = useCallback(() => {
    dispatch({ type: "APPLY_FILTERS" });

    const params = new URLSearchParams();
    state.selectedContentTypes.forEach((c) => params.append("format", c));
    state.selectedTags.forEach((tag) => params.append("tags", tag));
    if (state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID) {
      params.set("folder", state.selectedTreeItemId);
    }
    setSearchParams(params);
  }, [state.selectedContentTypes, state.selectedTags, state.selectedTreeItemId, setSearchParams]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: "SET_SEARCH_TEXT", payload: e.target.value.trim() });
  }, []);

  const handleSidebarToggle = useCallback((open: boolean) => {
    dispatch({ type: "SET_SIDEBAR_OPEN", payload: open });
  }, []);

  const handleExpandedItemsChange = useCallback(
    (_event: React.SyntheticEvent | null, itemIds: string[]) => {
      dispatch({ type: "SET_EXPANDED_ITEMS", payload: itemIds });
    },
    [],
  );
  const isFolderInArchivedPath = useCallback(
    (selectedId: string, archivedId: UUID): boolean => {
      if (!selectedId || selectedId === ROOT_ID) return false;
      let current: string | null = selectedId;
      while (current && current !== ROOT_ID) {
        if (current === archivedId) return true;
        current = folderMap.get(current as UUID) ?? null;
      }
      return false;
    },
    [folderMap],
  );

  const handleSetSelectedContentTypes = useCallback(
    (contentTypes: string[] | ((prev: string[]) => string[])) => {
      if (typeof contentTypes === "function") {
        // For functional updates, we'd need current state - but since ContentLibraryFilter
        // only passes arrays directly, we can handle this case if needed
        return;
      }
      dispatch({ type: "SET_SELECTED_CONTENT_TYPES", payload: contentTypes });
    },
    [],
  );

  const handleSetSelectedTags = useCallback((tags: string[] | ((prev: string[]) => string[])) => {
    if (typeof tags === "function") {
      return;
    }
    dispatch({ type: "SET_SELECTED_TAGS", payload: tags });
  }, []);

  const handleCreateSubFolder = useCallback(
    (parentFolderId: UUID | null) => {
      openCreateFolderModal({ parentFolderId });
    },
    [openCreateFolderModal],
  );

  const handleArchive = useCallback(
    (folderId: UUID | null) => {
      if (!folderId || !foldersData) {
        return;
      }
      openArchiveFolderModal({
        folderId,
        name: folderNameFromId(folderId, foldersData) ?? "",
      });
    },
    [foldersData, openArchiveFolderModal],
  );

  const handleMove = useCallback(
    (folderId: UUID) => {
      if (!folderId || !foldersData) {
        return;
      }
      openMoveFolderModal({
        folderId,
        name: folderNameFromId(folderId, foldersData) ?? "",
      });
    },
    [foldersData, openMoveFolderModal],
  );

  const CustomTreeItemWithReload = useCallback(
    (props: TreeItemProps) => (
      <CustomTreeItem
        {...props}
        reloadFolders={reloadFolders}
        onCreateSubFolder={handleCreateSubFolder}
        onArchive={handleArchive}
        onMove={handleMove}
        onItemDragStart={handleSidebarFolderDragStart}
        onItemDragOver={handleFolderDragOver}
        onItemDrop={(folderId, event) => void handleFolderDrop(folderId, event)}
      />
    ),
    [
      reloadFolders,
      handleCreateSubFolder,
      handleArchive,
      handleMove,
      handleSidebarFolderDragStart,
      handleFolderDragOver,
      handleFolderDrop,
    ],
  );

  const selectedParentFolderId =
    state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
      ? (state.selectedTreeItemId as UUID)
      : null;

  const handleOnClickFolder = useCallback(
    (folderId: string): void => {
      handleFolderSelect(folderId);
      setSearchFieldKey((prev) => prev + 1);
      dispatch({ type: "SET_SEARCH_TEXT", payload: "" });
    },
    [handleFolderSelect],
  );

  return (
    <>
      <Page
        title="Content Library | ChangeEngine"
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <ContentLibraryHeader
          onUpload={() => uploadModal.handleOpen({ parentFolderId: state.selectedTreeItemId })}
        />
        <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
          <ContentLibrarySidebar
            loading={foldersLoading}
            sidebarOpen={state.sidebarOpen}
            treeItems={treeItems}
            selectedTreeItemId={state.selectedTreeItemId}
            expandedItems={state.expandedItems}
            onToggle={handleSidebarToggle}
            onItemClick={handleOnClickFolder}
            onExpandedItemsChange={handleExpandedItemsChange}
            onCreateSubFolder={() =>
              createFolderModal.handleOpen({ parentFolderId: selectedParentFolderId })
            }
            itemSlot={CustomTreeItemWithReload}
          />
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <PageContent maxWidth="xl">
              {(loading || foldersLoading) && <CircularProgress />}
              {error && (
                <Alert severity="error">
                  Sorry, there was a problem retrieving your content. Please try again later
                </Alert>
              )}
              {!loading && !foldersLoading && filteredData && filteredFolders && (
                <ContentLibraryAssets
                  allFolders={foldersData ?? undefined}
                  filteredData={filteredData}
                  allFilteredData={filteredAllData}
                  filteredFolders={filteredFolders}
                  allFilteredFolders={filteredAllFolders}
                  onClickFolder={handleOnClickFolder}
                  state={state}
                  slots={{
                    breadcrumb: (
                      <FolderBreadcrumb
                        breadcrumbs={breadcrumbs}
                        onFolderSelect={handleOnClickFolder}
                      />
                    ),
                    searchField: (
                      <SearchField
                        key={searchFieldKey}
                        label="Search"
                        defaultValue={state.searchText}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                    ),
                    filter: (
                      <ContentLibraryFilter
                        handleApplyFilter={handleApplyFilter}
                        selectedContentTypes={state.selectedContentTypes}
                        setSelectedContentTypes={handleSetSelectedContentTypes}
                        contentTypes={allContentTypes}
                        selectedTags={state.selectedTags}
                        setSelectedTags={handleSetSelectedTags}
                        availableTags={availableTags}
                      />
                    ),
                    contentTypeChips: (
                      <ContentTypeChips
                        onDelete={handleRemoveContentTypeFilter}
                        contentTypes={state.appliedContentTypes}
                      />
                    ),
                    tagChips: (
                      <TagChips onDelete={handleRemoveTagFilter} tags={state.appliedTags} />
                    ),
                  }}
                  designer={account.data?.designer}
                  onSelectAsset={handleSelectAsset}
                  onCreateFolder={() =>
                    createFolderModal.handleOpen({ parentFolderId: selectedParentFolderId })
                  }
                  onArchiveFolder={() => {
                    if (
                      !foldersData ||
                      !state.selectedTreeItemId ||
                      state.selectedTreeItemId === ROOT_ID
                    ) {
                      return;
                    }
                    archiveFolderModal.handleOpen({
                      name: folderNameFromId(state.selectedTreeItemId as UUID, foldersData) ?? "",
                      folderId: state.selectedTreeItemId as UUID,
                    });
                  }}
                  onMoveFolder={() => {
                    if (
                      !foldersData ||
                      !state.selectedTreeItemId ||
                      state.selectedTreeItemId === ROOT_ID
                    ) {
                      return;
                    }
                    moveFolderModal.handleOpen({
                      name: folderNameFromId(state.selectedTreeItemId as UUID, foldersData) ?? "",
                      folderId: state.selectedTreeItemId as UUID,
                    });
                  }}
                  onAssetDragStart={handleAssetDragStart}
                  onFolderDragStart={handleFolderDragStart}
                  onFolderDragOver={handleFolderDragOver}
                  onFolderDrop={(folderId, event) => void handleFolderDrop(folderId, event)}
                />
              )}
              {data && data.length === 0 && <EmptyState />}
            </PageContent>
          </Box>
        </Box>
      </Page>
      <AssetPreviewModal
        asset={selectedAsset}
        open={previewModal.open}
        onClose={handleClosePreviewModal}
        onChange={reload}
        onArchived={handleArchived}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
        onFolderChange={reload}
        folders_feature_flag={true}
      />
      <UploadNewFileModal
        key={`upload-modal-${uploadModal.open ? "open" : "closed"}`}
        folders_feature_flag={true}
        open={uploadModal.open}
        onOpen={uploadModal.handleOpen}
        onClose={() => {
          uploadModal.handleClose();
          reloadFolders();
        }}
        onCancel={() => {
          uploadModal.handleClose();
          reloadFolders();
        }}
        onChange={(folderId) => {
          reload();
          const nextFolderId = folderId ?? state.selectedTreeItemId;
          if (nextFolderId) {
            handleFolderSelect(nextFolderId);
          }
        }}
        onViewFileClick={(asset) => {
          handleSelectAsset(asset);
        }}
        folderId={
          state.selectedTreeItemId && state.selectedTreeItemId !== ROOT_ID
            ? (state.selectedTreeItemId as UUID)
            : null
        }
      />
      <CreateFolderModal
        key={`create-folder-modal-${createFolderModal.open ? "open" : "closed"}`}
        open={createFolderModal.open}
        onClose={createFolderModal.handleClose}
        reloadFolders={reloadFolders}
        parentFolderId={createFolderModal.data?.parentFolderId ?? null}
      />
      {archiveFolderModal.data && (
        <ArchiveFolderModal
          open={archiveFolderModal.open}
          onClose={() => {
            archiveFolderModal.handleClose();
            if (
              archiveFolderModal.data?.folderId &&
              state.selectedTreeItemId &&
              isFolderInArchivedPath(state.selectedTreeItemId, archiveFolderModal.data.folderId)
            ) {
              const params = new URLSearchParams(searchParams);
              params.delete("folder");
              setSearchParams(params);
            }
          }}
          reloadFolders={reloadFolders}
          name={archiveFolderModal.data.name}
          folderId={archiveFolderModal.data.folderId}
        />
      )}
      {moveFolderModal.data && (
        <MoveFolderModal
          open={moveFolderModal.open}
          onClose={moveFolderModal.handleClose}
          reloadFolders={reloadFolders}
          name={moveFolderModal.data.name}
          folderId={moveFolderModal.data.folderId}
          folders={foldersData ?? null}
          loading={foldersLoading}
          error={foldersError ?? null}
        />
      )}
    </>
  );
};

export default ContentLibraryPage;
